import type { SDKState } from "@/state/sdk-state";
import { EmbedIframe } from "@/ui/embed-iframe";
import { EmbedBroker } from "./embed-broker";
import { EmbedNavigatorHandler } from "./embed-navigator-handler";
import { EmbedMessageType } from "@f3/shared/embed-messages";
import { joinURL } from "@/utils/url";
import { Logger } from "@/utils/logger";

export const EMBED_CONTAINER_ID = "fanfest-embed";
export const EMBED_ELEMENT_ID = "fanfest-embed-element";

export class EmbedService {
  #state: SDKState;
  #trackedContainers: Set<HTMLElement> = new Set();
  #embedElements: Set<HTMLIFrameElement> = new Set();
  #resizeHandles: Map<
    HTMLElement,
    { unsubscribe: () => void; resize: () => void }
  > = new Map();
  broker: EmbedBroker;
  #logger: Logger;
  #navigatorHandler: EmbedNavigatorHandler;

  constructor(state: SDKState, logger: Logger, broker?: EmbedBroker) {
    this.#state = state;
    this.#logger = logger.extend(this.constructor.name);
    this.broker = broker ?? new EmbedBroker(state, this.#logger);
    this.#navigatorHandler = new EmbedNavigatorHandler(
      this.#logger,
      this.#embedElements,
    );
  }

  public init() {
    this.#navigatorHandler.init();

    this.#state.channelInfo.value.subject.subscribe(
      (channelInfo) => {
        if (!channelInfo?.channel_id) return;

        this.#findElementsInContainer(document.body, channelInfo.channel_id);
        this.#setupMutationObserver(channelInfo.channel_id);
        this.broker.init();
      },
      { notifyOnSubscribe: true },
    );

    this.#state.authState.value.subject.subscribe(
      (result) => {
        if (this.#embedElements.size === 0) return;

        for (const element of this.#embedElements) {
          this.broker.sendMessage(
            {
              type: EmbedMessageType.Authentication,
              jwt: result?.authToken ?? null,
              matches_identity: result?.user !== null,
              auth_channel: result?.authChannelType ?? null,
              validated_identifiers: result?.validatedIdentifiers ?? [],
              integration_mode: this.#state.channelInfo.integrationMode ?? null,
            },
            element,
          );
        }
      },
      { notifyOnSubscribe: false },
    );

    this.broker.onMessage.subscribe((message) => {
      switch (message.type) {
        case EmbedMessageType.AuthenticationFinished: {
          const currentAuthState = this.#state.authState.value.get();
          if (currentAuthState?.user === null) {
            this.#state.authState.value.set({
              ...currentAuthState,
              user: message.user,
            });
          } else if (currentAuthState === null) {
            this.#logger.warn(
              "[EmbedService] Authentication finished message received but no auth state is set, ignoring",
            );
          } else {
            this.#logger.warn(
              "[EmbedService] Authentication finished message received but user is already authenticated, ignoring",
            );
          }
          break;
        }
        case EmbedMessageType.EmbedReady: {
          for (const element of this.#embedElements) {
            const result = this.#state.authState.value.get();

            this.broker.sendMessage(
              {
                type: EmbedMessageType.Authentication,
                jwt: result?.authToken ?? null,
                matches_identity: result?.user !== null,
                auth_channel: result?.authChannelType ?? null,
                validated_identifiers: result?.validatedIdentifiers ?? [],
                integration_mode:
                  this.#state.channelInfo.integrationMode ?? null,
              },
              element,
            );
          }
          break;
        }
        case EmbedMessageType.Logout: {
          this.#state.authState.value.remove();
          break;
        }
        case EmbedMessageType.ModalStateChanged: {
          this.#handleEmbedModalStateChange(message.isOpen);
          break;
        }
        case EmbedMessageType.DisplayModeChanged: {
          this.#handleDisplayModeChange(message.mode);
          break;
        }
      }
    });
  }

  // ---- Embed display mode management ----

  /**
   * The current display mode requested by the iframe content.
   * - `auto-height`: resizer controls iframe height (default)
   * - `viewport-locked`: iframe is locked to the host viewport height
   *   (used by show pages that need to fill the screen)
   */
  #displayMode: "auto-height" | "viewport-locked" = "auto-height";
  #viewportResizeHandler: (() => void) | null = null;

  /**
   * Lock all embed iframes to the host viewport height. Used by both
   * the modal handler (temporarily) and the display-mode handler
   * (persistently for show pages).
   */
  #lockIframesToViewport() {
    const vh = `${window.innerHeight}px`;
    for (const element of this.#embedElements) {
      element.style.height = vh;
      element.style.maxHeight = vh;
    }
  }

  /**
   * Release the viewport lock — remove fixed height so the auto-resize
   * observer can take control again.
   */
  #unlockIframes() {
    for (const element of this.#embedElements) {
      element.style.height = "";
      element.style.maxHeight = "";
    }
  }

  #startViewportResizeListener() {
    if (this.#viewportResizeHandler) return;
    this.#viewportResizeHandler = () => this.#lockIframesToViewport();
    window.addEventListener("resize", this.#viewportResizeHandler);
  }

  #stopViewportResizeListener() {
    if (!this.#viewportResizeHandler) return;
    window.removeEventListener("resize", this.#viewportResizeHandler);
    this.#viewportResizeHandler = null;
  }

  /**
   * When a modal opens inside the embed iframe while auto-height is
   * enabled, the iframe stretches to fit the modal's full-page overlay,
   * but the host page remains scrollable and the modal isn't centred in
   * the visible viewport.
   *
   * Fix: lock the iframe to the host viewport height so
   * `position: fixed; inset: 0` inside the iframe covers exactly the
   * visible area. Lock host body scroll and scroll the iframe into view.
   * On close, restore the iframe's display-mode-appropriate height.
   */
  #handleEmbedModalStateChange(isOpen: boolean) {
    if (!this.#state.options.enableAutoHeightEmbed) return;

    if (isOpen) {
      this.#lockIframesToViewport();
      this.#startViewportResizeListener();
      document.body.style.overflow = "hidden";
      const firstEmbed = this.#embedElements.values().next().value;
      firstEmbed?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      document.body.style.overflow = "";
      // Restore whatever display mode the page was using before the modal
      if (this.#displayMode === "viewport-locked") {
        // Keep locked — the page wants viewport height
      } else {
        this.#unlockIframes();
        this.#stopViewportResizeListener();
      }
    }
  }

  /**
   * Switch the iframe between auto-height (resizer-controlled) and
   * viewport-locked (fills the host viewport). Show pages use
   * viewport-locked so they fill the screen instead of collapsing.
   */
  #handleDisplayModeChange(mode: "auto-height" | "viewport-locked") {
    this.#displayMode = mode;

    if (mode === "viewport-locked") {
      this.#lockIframesToViewport();
      this.#startViewportResizeListener();
    } else {
      this.#unlockIframes();
      this.#stopViewportResizeListener();
    }
  }

  detach() {
    this.broker.detach();
    this.#navigatorHandler.destroy();
    for (const handle of this.#resizeHandles.values()) {
      handle.unsubscribe();
    }
    this.#resizeHandles.clear();
  }

  #findElementsInContainer(container: HTMLElement, channelId: string) {
    const element = container.querySelector(`#${EMBED_CONTAINER_ID}`);
    if (element && element instanceof HTMLElement) {
      this.#trackContainer(element, channelId);
    }
  }

  #setupMutationObserver(channelId: string) {
    const handleMutation = (mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        // Skip if the target is an embed element, otherwise an infinite loop will occur
        if (
          mutation.target instanceof HTMLElement &&
          this.#embedElements.has(mutation.target as HTMLIFrameElement)
        ) {
          continue;
        }

        if (
          mutation.attributeName === "id" &&
          mutation.target instanceof HTMLElement
        ) {
          const element = mutation.target as HTMLElement;
          if (element.id === EMBED_CONTAINER_ID) {
            this.#trackContainer(element, channelId);
          }
        }

        if (mutation.addedNodes) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (!(node instanceof HTMLElement)) continue;

            if (node.id === EMBED_CONTAINER_ID) {
              this.#trackContainer(node, channelId);
            } else if (node.childElementCount > 0) {
              this.#findElementsInContainer(node, channelId);
            }
          }
        }

        if (mutation.removedNodes.length > 0) {
          for (const node of Array.from(mutation.removedNodes)) {
            if (node instanceof HTMLElement && node.id === EMBED_CONTAINER_ID) {
              this.#untrackContainer(node);
            }
          }
        }
      }
    };

    const observer = new MutationObserver(handleMutation);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["id"],
    });
  }

  #trackContainer(element: HTMLElement, channelId: string) {
    if (this.#trackedContainers.has(element)) {
      return;
    }

    this.#trackedContainers.add(element);
    this.#setupElement(element, channelId);
  }

  #untrackContainer(element: HTMLElement) {
    if (!this.#trackedContainers.has(element)) {
      return;
    }

    this.#trackedContainers.delete(element);

    const handle = this.#resizeHandles.get(element);
    if (handle) {
      handle.unsubscribe();
      this.#resizeHandles.delete(element);
    }

    // Fix pre-existing bug: find and remove the iframe from #embedElements.
    // The old code did this.#embedElements.delete(element as HTMLIFrameElement)
    // which was always a no-op — element is the container, not an iframe.
    for (const iframe of this.#embedElements) {
      if (element.contains(iframe)) {
        this.#embedElements.delete(iframe);
        break;
      }
    }
  }

  #setupElement(element: HTMLElement, channelId: string) {
    const iframe = this.#createIframe(channelId);
    element.innerHTML = "";

    this.#embedElements.add(iframe);
    element.appendChild(iframe);

    if (this.#state.options.enableAutoHeightEmbed) {
      this.#initAutoResize(element, iframe);
    }
  }

  async #initAutoResize(container: HTMLElement, iframe: HTMLIFrameElement) {
    try {
      const { initialize } = await import("@open-iframe-resizer/core");
      const results = await initialize({}, iframe);
      const result = results[0];

      // Guard: container may have been removed during the async init
      if (!this.#trackedContainers.has(container)) {
        result?.unsubscribe();
        return;
      }

      if (result) {
        this.#resizeHandles.set(container, result);
      }
    } catch (error) {
      this.#logger.error("Failed to initialize iframe auto-resize", { error });
    }
  }

  #createIframe(channelId: string) {
    const src = new URL(joinURL(this.#state.options.appOrigin, channelId));
    src.searchParams.set("embed", "true");

    // Forward the channel's integration mode so the frontend knows
    // synchronously whether in-iframe auth (Fanfest Auth) is allowed.
    // The embed broker handshake will also propagate it after load.
    const integrationMode = this.#state.channelInfo.integrationMode;
    if (integrationMode) {
      src.searchParams.set("integration_mode", integrationMode);
    }

    return EmbedIframe({
      id: EMBED_ELEMENT_ID,
      src: src.toString(),
      backgroundColor: this.#state.options.iframe.backgroundColor,
      enableAutoHeightEmbed: this.#state.options.enableAutoHeightEmbed,
    });
  }
}
